import React, { useState, useLayoutEffect, useRef } from 'react';
import { ResponseItem } from '../types';
import { apiService } from '../services/api';
import Modal from './Modal';
import {
  Card,
  ComparisonContainer,
  TextColumn,
  ColumnTitle,
  TextDisplay,
  EditableTextArea,
  MetadataContainer,
  MetadataItem,
  TagButton,
  ProcessButton,
} from './StyledComponents';
import styled from 'styled-components';

const ViewInManualButton = styled.button`
  background: none;
  color: #333;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 1.2rem;
  font-weight: 500;
  line-height: 1.2;
  text-decoration: underline;
  white-space: nowrap;
  
  &:hover {
    color: #000;
  }
  
  &:disabled {
    color: #999;
    cursor: not-allowed;
    text-decoration: none;
  }
`;

const HeaderRowSpacer = styled(ViewInManualButton)`
  visibility: hidden;
  pointer-events: none;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 32px;
  margin-bottom: 1rem;
  gap: 1rem;

  h3 {
    margin: 0;
  }
`;

const StatusTag = styled.div<{ isConfirmed: boolean; isRejected: boolean; isExpanded: boolean }>`
  background: ${props => props.isRejected ? '#c0392b' : props.isConfirmed ? '#000000' : '#555'};
  color: white;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: ${props => props.isExpanded ? '1rem' : '0.25rem'};
  display: flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
`;

const RejectButton = styled.button`
  background: #c0392b;
  color: white;
  border: none;
  border-radius: 4px;
  width: 36px;
  height: 36px;
  cursor: pointer;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    background: #a93226;
  }

  &:disabled {
    background: #999;
    cursor: not-allowed;
  }
`;


interface ResponseItemsProps {
  responseItems: ResponseItem[];
  currentManualType: string;
  useCaseIdx: number;
  onItemConfirmed?: (index: number, updatedManualText: string, appliedText: string) => void;
  onItemUnconfirmed?: (index: number) => Promise<void>;
  onItemRejected?: (index: number) => void;
  currentManualText?: string;
}

// Sub-component for a single response item to manage its own state and layout
const ResponseItemCard: React.FC<{ 
  item: ResponseItem; 
  index: number;
  useCaseIdx: number;
  currentManualType: string;
  onItemConfirmed?: (index: number, updatedManualText: string, appliedText: string) => void;
  onItemUnconfirmed?: (index: number) => Promise<void>;
  onItemRejected?: (index: number) => void;
  currentManualText?: string;
}> = ({ item, index, useCaseIdx, currentManualType, onItemConfirmed, onItemUnconfirmed, onItemRejected, currentManualText }) => {
  const [editedText, setEditedText] = useState(item.updated_text);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isExpanded, setIsExpanded] = useState(!item.isConfirmed && !item.isRejected);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasBeenConfirmed, setHasBeenConfirmed] = useState(false);
  const textDisplayRef = useRef<HTMLDivElement>(null);
  const editableTextRef = useRef<HTMLTextAreaElement>(null);

  // Use the item's isConfirmed / isRejected state directly
  const isConfirmed = item.isConfirmed || false;
  const isRejected = item.isRejected || false;

  // Sync editedText with item updates
  React.useEffect(() => {
    setEditedText(item.updated_text);
  }, [item.updated_text]);

  // Debug logging
  if (import.meta.env.DEV) {
    console.log('ResponseItemCard rendered:', { 
      index, 
      isConfirmed, 
      itemIsConfirmed: item.isConfirmed,
      hasAcceptButton: !isConfirmed
    });
  }

  // Auto-expand textarea to fit content
  useLayoutEffect(() => {
    if (!isExpanded) return;
    const el = editableTextRef.current;
    if (!el) return;

    // Force a recompute when (re)expanding so the textarea fits all content.
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [editedText, isExpanded]);

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditedText(e.target.value);
  };

  const handleReject = () => {
    if (isRejected) {
      // Un-reject
      if (onItemRejected) onItemRejected(index);
      return;
    }
    if (onItemRejected) {
      onItemRejected(index);
    }
    setIsExpanded(false);
  };

  const handleAccept = async () => {
    // If already confirmed and clicking "Bearbeiten", toggle back to unconfirmed
    if (isConfirmed) {
      if (onItemUnconfirmed) {
        setIsConfirming(true);
        try {
          await onItemUnconfirmed(index);
        } catch (error) {
          console.error('Error unconfirming change:', error);
        } finally {
          setIsConfirming(false);
        }
      }
      return;
    }
    
    setIsConfirming(true);
    try {
      const replacementSourceText = item.lastAppliedText || item.original_text;
      const isTextChanged = editedText !== item.updated_text;
      const response = await apiService.confirmChange(
        !isTextChanged, // accepted: true if text wasn't changed, false if it was modified
        replacementSourceText,
        editedText,
        currentManualType,
        useCaseIdx,
        index,
        isTextChanged ? editedText : undefined
      );
      
      // Let the parent component handle the state update
      if (onItemConfirmed) {
        onItemConfirmed(index, response.manual_text, editedText);
      }
      setHasBeenConfirmed(true);
      setIsExpanded(false);
    } catch (error) {
      console.error('Error confirming change:', error);
    } finally {
      setIsConfirming(false);
    }
  };

  const handleViewInManual = () => {
    setIsModalOpen(true);
  };

  return (
    <Card key={index}>
      <TagButton>{item.category}</TagButton>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '1.5rem',
            cursor: 'pointer',
            padding: '0',
            margin: '0',
            color: '#34495e',
            transition: 'transform 0.3s ease',
            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: '1',
            minWidth: '24px',
            minHeight: '24px',
          }}
          aria-label={isExpanded ? 'Einklappen' : 'Ausklappen'}
        >
          ❯
        </button>
        <ColumnTitle style={{ marginTop: 0, marginBottom: 0 }}>{item.location}</ColumnTitle>
      </div>
      <StatusTag isConfirmed={isConfirmed} isRejected={isRejected} isExpanded={isExpanded}>
        {isConfirmed ? '✓ Bestätigt' : isRejected ? '✗ Abgelehnt' : 'Zu prüfen'}
      </StatusTag>
      {isExpanded && (
      <>
      <MetadataContainer>
        <MetadataItem><strong>Context:</strong> {item.context}</MetadataItem>
        <MetadataItem><strong>Reasoning:</strong> {item.reasoning}</MetadataItem>
      </MetadataContainer>
      <ComparisonContainer>
        <TextColumn>
          <HeaderRow>
            <ColumnTitle>Original Text</ColumnTitle>
            <ViewInManualButton onClick={handleViewInManual} disabled={!currentManualText}>
              Im Kontext anzeigen
            </ViewInManualButton>
          </HeaderRow>
          <TextDisplay ref={textDisplayRef}>{item.original_text}</TextDisplay>
        </TextColumn>
        <TextColumn>
          <HeaderRow>
            <ColumnTitle>Updated Text (Editable)</ColumnTitle>
            <HeaderRowSpacer tabIndex={-1} aria-hidden="true">
              Im Kontext anzeigen
            </HeaderRowSpacer>
          </HeaderRow>
          <EditableTextArea
            ref={editableTextRef}
            value={editedText}
            onChange={handleTextChange}
            placeholder="Edit the updated text here..."
            disabled={isConfirmed}
            $lockResize={hasBeenConfirmed}
          />
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', marginTop: '10px' }}>
            <ProcessButton 
              onClick={handleAccept} 
              disabled={(isConfirming && !isConfirmed) || isRejected}
              style={{ display: 'inline-flex', margin: 0, alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
            >
              {isConfirming ? (
                <>✓ Bestätige...</>
              ) : isConfirmed ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                  </svg>
                  Bearbeiten
                </>
              ) : (
                <>✓ Bestätigen</>
              )}
            </ProcessButton>
            <RejectButton
              onClick={handleReject}
              disabled={isConfirmed}
              title={isRejected ? 'Ablehnung rückgängig machen' : 'Ablehnen'}
            >
              {isRejected ? '↩' : '✕'}
            </RejectButton>
          </div>
        </TextColumn>
      </ComparisonContainer>
      <Modal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        content={currentManualText || ''}
        manualType={currentManualType}
        highlightText={isConfirmed ? editedText : item.original_text}
      />
      </>
      )}
    </Card>
  );
};

const ResponseItems: React.FC<ResponseItemsProps> = ({ responseItems, currentManualType, useCaseIdx, onItemConfirmed, onItemUnconfirmed, onItemRejected, currentManualText }) => {
  return (
    <div>
      {responseItems.map((item, index) => (
        <ResponseItemCard 
          key={item.id || `${item.category}-${item.location}-${index}`} 
          item={item} 
          index={index}
          useCaseIdx={useCaseIdx}
          currentManualType={currentManualType}
          onItemConfirmed={onItemConfirmed}
          onItemUnconfirmed={onItemUnconfirmed}
          onItemRejected={onItemRejected}
          currentManualText={currentManualText}
        />
      ))}
    </div>
  );
};

export default ResponseItems;
